import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import debounce from 'lodash.debounce';
import { InfoTooltip } from '../Common/InfoTooltip';
import '../../styles/SpecialsAndMenus.css';

function SpecialsAndMenus() {
  // State for specials and menus
  const [specials, setSpecials] = useState([]);
  const [menus, setMenus] = useState([]);
  
  // State for forms and editing
  const [newSpecial, setNewSpecial] = useState({
    name: '',
    description: '',
    value: '',
    image: null,
    imagePreview: null, 
    weblink: '',
  });
  const [newMenu, setNewMenu] = useState({
    pdfFile: null,
    weblink: '',
  });
  
  // UI state
  const [isCreatingSpecial, setIsCreatingSpecial] = useState(false);
  const [isCreatingMenu, setIsCreatingMenu] = useState(false);
  const [editingSpecial, setEditingSpecial] = useState(null);
  const [editingMenu, setEditingMenu] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState('specials'); // 'specials' or 'menus'
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // References
  const specialsHeaderRef = useRef(null);
  const fileInputRef = useRef(null);
  const menuFileInputRef = useRef(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    message: '',
    onConfirm: null,
    onCancel: null
  });

  // Toast notification helper
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  // Handle clicks outside dropdown menus
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Fetch specials data
  const fetchSpecials = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/vendors/specials?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpecials(prev => page === 1 ? response.data.specials : [...prev, ...response.data.specials]);
      setHasMore(response.data.hasMore);
    } catch (error) {
      console.error('Error fetching specials:', error);
      showToast('error', 'Failed to fetch specials. Please try again.');
    }
  }, [page, showToast]);

  const triggerFileInput = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };
    
  // Fetch menus data
  const fetchMenus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/vendors/menus', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenus(response.data);
    } catch (error) {
      console.error('Error fetching menus:', error);
      showToast('error', 'Failed to fetch menus. Please try again.');
    }
  }, [showToast]);
											  
  // Debounced data fetching
  const debouncedFetchData = useCallback(
    debounce(() => {
      setIsLoading(true);
      Promise.all([fetchSpecials(), fetchMenus()])
        .catch(error => console.error('Error fetching data:', error))
        .finally(() => setIsLoading(false));
    }, 300),
    [fetchSpecials, fetchMenus]
  );

  // Initial data loading
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
    debouncedFetchData();

    return () => {
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData]);


// Form validation for specials
const validateSpecialForm = (data) => {
  const errors = {};
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Special name is required';
  }
  // Handle empty value to prevent database errors
  if (data.value === '') {
    data.value = null;
  }
  setFormErrors(errors);
  return Object.keys(errors).length === 0;
};

  // Form validation for menus
  const validateMenuForm = (data) => {
    const errors = {};
    if (!data.pdfFile && !data.weblink) {
      errors.general = 'Either a PDF file or weblink is required';
    }
    if (data.weblink && !isValidUrl(data.weblink)) {
      errors.weblink = 'Please enter a valid URL';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // URL validation helper
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handle special form input change
const handleSpecialChange = (e) => {
  const { name, value, files } = e.target;

  // Handle image file uploads and create preview
  if (files && name === 'image') {
    // Create a temporary URL for the selected file
    const imageUrl = URL.createObjectURL(files[0]);
    
    if (editingSpecial) {
      setEditingSpecial(prev => ({
        ...prev,
        image: files[0],
        imagePreview: imageUrl // Store the preview URL in state
      }));
    } else {
      setNewSpecial(prev => ({
        ...prev,
        image: files[0],
        imagePreview: imageUrl // Store the preview URL in state
      }));
    }
  } else {
    // Handle regular form field changes
    if (editingSpecial) {
      setEditingSpecial(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNewSpecial(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }

  // Clear validation error when field is corrected
  if (formErrors[name]) {
    setFormErrors(prev => ({
      ...prev,
      [name]: null
    }));
  }
};


  // Handle menu form input change
  const handleMenuChange = (e) => {
    const { name, value, files } = e.target;

    // Display file name for selected PDF
if (files && name === 'pdfFile') {
  const fileNameElement = document.getElementById('menu-file-name');
  if (fileNameElement) {
    fileNameElement.textContent = files[0].name;
  }
}
    
    if (editingMenu) {
      setEditingMenu(prev => ({
        ...prev,
        [name]: files ? files[0] : value
      }));
    } else {
      setNewMenu(prev => ({
        ...prev,
        [name]: files ? files[0] : value
      }));
    }

    // Clear validation error when field is corrected
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Start editing a special
const startEditingSpecial = (special) => {
  setEditingSpecial({
    ...special,
    image: null,
    imagePreview: special.image_url // Store the existing image URL as preview
  });
  setIsCreatingSpecial(true);
  setIsCreatingMenu(false);
  // Scroll to the form
  if (specialsHeaderRef.current) {
    specialsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

  // Start editing a menu
  const startEditingMenu = (menu) => {
    // Create a copy without the file (since we can't repopulate file inputs)
    setEditingMenu({
      ...menu,
      pdfFile: null
    });
    setIsCreatingMenu(true);
    setIsCreatingSpecial(false);
    // Scroll to the form
    if (specialsHeaderRef.current) {
      specialsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Cancel editing
const cancelEditing = () => {
  setEditingSpecial(null);
  setEditingMenu(null);
  setIsCreatingSpecial(false);
  setIsCreatingMenu(false);
  setFormErrors({});
  setNewSpecial({
    name: '',
    description: '',
    value: '',
    image: null,
    imagePreview: null, // Reset this property
    weblink: '',
  });
  setNewMenu({
    pdfFile: null,
    weblink: '',
  });
};

  // Handle special form submission
  const handleSpecialSubmit = async (e) => {
    e.preventDefault();
    
    const formData = editingSpecial || newSpecial;
    
    if (!validateSpecialForm(formData)) {
      return;
    }

    setIsSaving(true);
    
    try {
      const submitFormData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          submitFormData.append(key, formData[key]);
        }
      });

      if (editingSpecial) {
        await axios.put(`/api/vendors/specials/${editingSpecial.id}`, submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', 'Special updated successfully');
      } else {
        await axios.post('/api/vendors/specials', submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', 'Special created successfully');
      }
      
      setNewSpecial({
        name: '',
        description: '',
        value: '',
        image: null,
        weblink: '',
      });
      setEditingSpecial(null);
      setIsCreatingSpecial(false);
      setPage(1);
      fetchSpecials();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error saving special:', error);
      showToast('error', error.response?.data?.error || 'Failed to save special');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle menu form submission
  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    
    const formData = editingMenu || newMenu;
    
    if (!validateMenuForm(formData)) {
      return;
    }

    setIsSaving(true);
    
    try {
      const submitFormData = new FormData();
      if (formData.pdfFile) {
        submitFormData.append('pdfFile', formData.pdfFile);
      }
      if (formData.weblink) {
        submitFormData.append('weblink', formData.weblink);
      }

      if (editingMenu) {
        await axios.put(`/api/vendors/menus/${editingMenu.id}`, submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', 'Menu updated successfully');
      } else {
        await axios.post('/api/vendors/menus', submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', 'Menu created successfully');
      }
      
      setNewMenu({
        pdfFile: null,
        weblink: '',
      });
      setEditingMenu(null);
      setIsCreatingMenu(false);
      fetchMenus();
      
      // Reset file input
      if (menuFileInputRef.current) {
        menuFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error saving menu:', error);
      showToast('error', error.response?.data?.error || 'Failed to save menu');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle special deletion
  const handleDeleteSpecial = (id) => {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this special? This action cannot be undone.',
      onConfirm: async () => {
        setIsSaving(true);
        try {
          await axios.delete(`/api/vendors/specials/${id}`);
          showToast('success', 'Special deleted successfully');
          setPage(1);
          fetchSpecials();
        } catch (error) {
          console.error('Error deleting special:', error);
          showToast('error', 'Failed to delete special');
        } finally {
          setIsSaving(false);
          setConfirmDialog({ show: false });
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false });
      }
    });
  };

  // Handle menu deletion
  const handleDeleteMenu = (id) => {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this menu? This action cannot be undone.',
      onConfirm: async () => {
        setIsSaving(true);
        try {
          await axios.delete(`/api/vendors/menus/${id}`);
          showToast('success', 'Menu deleted successfully');
          fetchMenus();
        } catch (error) {
          console.error('Error deleting menu:', error);
          showToast('error', 'Failed to delete menu');
        } finally {
          setIsSaving(false);
          setConfirmDialog({ show: false });
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false });
      }
    });
  };

  // Handle clicking "load more" for specials
  const loadMoreSpecials = () => {
    setPage(prevPage => prevPage + 1);
  };

  // Handle dropdown menu toggle
  const handleMenuToggle = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenMenuId(openMenuId === id ? null : id);
  };

  // Main render function
  if (isLoading) {
    return <div className="sm-loading">Loading menus and specials...</div>;
  }

  return (
    <div className="sm-container">
      {/* Toast notification */}
      {toast && (
        <div className={`sm-toast ${toast.type}`}>
          <div className="sm-toast-content">
            <span className={`sm-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="sm-toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmDialog.show && (
        <div className="sm-confirm-dialog-overlay">
          <div className="sm-confirm-dialog">
            <p>{confirmDialog.message}</p>
            <div className="sm-confirm-actions">
              <button 
                className="sm-button secondary" 
                onClick={confirmDialog.onCancel}
              >
                Cancel
              </button>
              <button 
                className="sm-button primary" 
                onClick={confirmDialog.onConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sm-header">
        <div className="sm-header-content">
          <h1>Specials & Menus</h1>
          <p className="sm-header-subtitle">Manage your specials and menu offerings</p>
        </div>
      </div>

      {/* Top tabs for switching between specials and menus */}
      <div className="sm-tabs" ref={specialsHeaderRef}>
        <button 
          className={`sm-tab ${activeTab === 'specials' ? 'active' : ''}`}
          onClick={() => setActiveTab('specials')}
        >
          Specials
        </button>
        <button 
          className={`sm-tab ${activeTab === 'menus' ? 'active' : ''}`}
          onClick={() => setActiveTab('menus')}
        >
          Menus
        </button>
      </div>

      {/* Specials Section */}
      {activeTab === 'specials' && (
        <>
          {/* Create/Edit Special Form */}
          {isCreatingSpecial && (
            <div className="sm-card">
              <div className="sm-card-header">
                <h2>{editingSpecial ? 'Edit Special' : 'Create New Special'}</h2>
                <button 
                  type="button" 
                  onClick={cancelEditing} 
                  className="sm-button-icon"
                  aria-label="Close form"
                >
                  ×
                </button>
              </div>
              <div className="sm-card-body">
                <form onSubmit={handleSpecialSubmit} className="sm-form">
                  <div className="sm-form-grid">
                    <div className={`sm-form-group ${formErrors.name ? 'error' : ''}`}>
                      <label className="sm-label" htmlFor="special-name">
                        Special Name <span className="sm-required">*</span>
                      </label>
                      <input
                        type="text"
                        id="special-name"
                        name="name"
                        value={editingSpecial ? editingSpecial.name : newSpecial.name}
                        onChange={handleSpecialChange}
                        className="sm-input"
                        placeholder="e.g., Happy Hour 2-for-1"
                      />
                      {formErrors.name && (
                        <span className="sm-error-message">{formErrors.name}</span>
                      )}
                    </div>

                    <div className={`sm-form-group ${formErrors.value ? 'error' : ''}`}>
                      <label className="sm-label" htmlFor="special-value">
                        Price (R)
                        <InfoTooltip 
                          text="The price or value of this special offer" 
                          variant="vendor"
                        />
                      </label>
                      <input
                        type="number"
                        id="special-value"
                        name="value"
                        value={editingSpecial ? editingSpecial.value || '' : newSpecial.value || ''}
                        onChange={handleSpecialChange}
                        className="sm-input"
                        placeholder="e.g., 99.99"
                        step="0.01"
                        min="0"
                      />
                      {formErrors.value && (
                        <span className="sm-error-message">{formErrors.value}</span>
                      )}
                    </div>

                    <div className={`sm-form-group sm-full-width ${formErrors.description ? 'error' : ''}`}>
                      <label className="sm-label" htmlFor="special-description">
                        Description
                      </label>
                      <textarea
                        id="special-description"
                        name="description"
                        value={editingSpecial ? editingSpecial.description : newSpecial.description}
                        onChange={handleSpecialChange}
                        className="sm-input sm-textarea"
                        placeholder="Describe your special offer"
                        rows="3"
                      ></textarea>
                      {formErrors.description && (
                        <span className="sm-error-message">{formErrors.description}</span>
                      )}
                    </div>

                    <div className="sm-form-group">
                  <label className="sm-label" htmlFor="special-image">
                    Image
                    <InfoTooltip 
                      text="Upload an image to display with your special" 
                      variant="vendor"
                    />
                  </label>
                  {/* <div 
                    className="sm-upload-wrapper" 
                    onClick={() => triggerFileInput(fileInputRef)}
                  >
                    <div className="sm-upload-placeholder">
                      <div className="sm-upload-icon">+</div>
                      <span>Click to upload an image</span>
                      <span className="sm-upload-hint">JPG, PNG or GIF up to 10MB</span>
                    </div>
                    <input
                      type="file"
                      id="special-image"
                      name="image"
                      onChange={handleSpecialChange}
                      className="sm-file-input-hidden"
                      accept="image/*"
                      ref={fileInputRef}
                    />
                  </div> */}
  <div className="sm-upload-wrapper" onClick={() => triggerFileInput(fileInputRef)}>
    {/* Show preview image if available */}
    {(editingSpecial?.imagePreview || editingSpecial?.image_url || newSpecial?.imagePreview) ? (
      <div className="sm-current-image" style={{textAlign: 'center'}}>
        <img 
          src={editingSpecial?.imagePreview || editingSpecial?.image_url || newSpecial?.imagePreview} 
          alt="Special preview" 
          style={{maxWidth: '100%', maxHeight: '200px', margin: '10px auto', display: 'block'}} 
        />
        <p className="sm-help-text">Click to change image</p>
      </div>
    ) : (
      <div className="sm-upload-placeholder">
        <div className="sm-upload-icon">+</div>
        <span>Click to upload an image</span>
        <span className="sm-upload-hint">JPG, PNG or GIF up to 10MB</span>
      </div>
    )}
    <input
      type="file"
      id="special-image"
      name="image"
      onChange={handleSpecialChange}
      className="sm-file-input-hidden"
      accept="image/*"
      ref={fileInputRef}
    />
  </div>
   
                </div>

                    <div className="sm-form-group">
                      <label className="sm-label" htmlFor="special-weblink">
                        Web Link
                        <InfoTooltip 
                          text="Optional URL for more information" 
                          variant="vendor"
                        />
                      </label>
                      <input
                        type="url"
                        id="special-weblink"
                        name="weblink"
                        value={editingSpecial ? editingSpecial.weblink || '' : newSpecial.weblink || ''}
                        onChange={handleSpecialChange}
                        className="sm-input"
                        placeholder="https://example.com/special-offer"
                      />
                    </div>
                  </div>

                  <div className="sm-form-actions">
                    <button 
                      type="button" 
                      onClick={cancelEditing} 
                      className="sm-button secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="sm-button primary"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <span className="sm-spinner"></span> 
                          {editingSpecial ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingSpecial ? 'Update Special' : 'Create Special'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Specials List */}
          {!isCreatingSpecial && (
            <>
           <div className="sm-actions sm-actions-left">
            <button 
              type="button" 
              onClick={() => {
                setIsCreatingSpecial(true);
                setIsCreatingMenu(false);
                setEditingSpecial(null);
                setEditingMenu(null);
              }} 
              className="sm-button primary"
            >
              Create New Special
            </button>
          </div>

              {specials.length === 0 ? (
                <div className="sm-empty-state">
                  <div className="sm-empty-icon">🍽️</div>
                  <h3>No specials yet</h3>
                  <p>Create your first special offer to promote to your customers</p>
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsCreatingSpecial(true);
                      setIsCreatingMenu(false);
                    }} 
                    className="sm-button primary"
                  >
                    Create First Special
                  </button>
                </div>
              ) : (
                <div className="sm-grid">
                  {specials.map(special => (
                    <div key={special.id} className="sm-card special-card">
                      <div className="sm-card-header sm-card-special-header">
                        <h3>{special.name}</h3>
                        <div className="sm-card-actions">
                          <button 
                            className="sm-button secondary small"
                            onClick={() => startEditingSpecial(special)}
                          >
                            Edit
                          </button>
                          <div className={`sm-menu-dropdown ${openMenuId === `special-${special.id}` ? 'open' : ''}`}>
                            <button 
                              className="sm-menu-button" 
                              onClick={(e) => handleMenuToggle(e, `special-${special.id}`)} 
                              aria-label="Special options"
                            >
                              •••
                            </button>
                            <div className="sm-menu-content">
                              <button 
                                type="button" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSpecial(special.id);
                                }}
                                className="sm-menu-item danger"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="sm-card-body">
                        {special.image_url && (
                          <div className="sm-image-container">
                            <img src={special.image_url} alt={special.name} className="sm-special-image" />
                          </div>
                        )}
                        <div className="sm-special-details">
                          <p className="sm-special-description">{special.description}</p>
                          {special.value && (
                            <p className="sm-special-value">R{special.value}</p>
                          )}
                          {special.weblink && (
                            <a 
                              href={special.weblink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="sm-weblink"
                            >
                              View Details Online
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasMore && (
                <div className="sm-load-more">
                  <button 
                    onClick={loadMoreSpecials} 
                    className="sm-button secondary"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="sm-spinner"></span> Loading...
                      </>
                    ) : (
                      'Load More Specials'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Menus Section */}
      {activeTab === 'menus' && (
        <>
          {/* Create/Edit Menu Form */}
          {isCreatingMenu && (
            <div className="sm-card">
              <div className="sm-card-header">
                <h2>{editingMenu ? 'Edit Menu' : 'Create New Menu'}</h2>
                <button 
                  type="button" 
                  onClick={cancelEditing} 
                  className="sm-button-icon"
                  aria-label="Close form"
                >
                  ×
                </button>
              </div>
              <div className="sm-card-body">
                <form onSubmit={handleMenuSubmit} className="sm-form">
                  {formErrors.general && (
                    <div className="sm-form-error">
                      <span className="sm-error-message">{formErrors.general}</span>
                    </div>
                  )}
                  
                  <div className="sm-form-group">
                <label className="sm-label" htmlFor="menu-file">
                  Upload Menu PDF
                  <InfoTooltip 
                    text="Upload a PDF version of your menu" 
                    variant="vendor"
                  />
                </label>
                {/* <div 
                  className="sm-upload-wrapper" 
                  onClick={() => triggerFileInput(menuFileInputRef)}
                >
                  <div className="sm-upload-placeholder">
                    <div className="sm-upload-icon">📄</div>
                    <span>Click to upload a PDF</span>
                    <span className="sm-upload-hint">PDF files up to 10MB</span>
                  </div>
                  <input
                    type="file"
                    id="menu-file"
                    name="pdfFile"
                    onChange={handleMenuChange}
                    className="sm-file-input-hidden"
                    accept="application/pdf"
                    ref={menuFileInputRef}
                  />
                </div> */}
                <div className="sm-upload-wrapper" onClick={() => triggerFileInput(menuFileInputRef)}>
  {/* Show either current PDF info or upload placeholder */}
  {(editingMenu && editingMenu.pdf_url) || document.getElementById('menu-file-name')?.textContent || newMenu.pdfFile ? (
    <div className="sm-current-file" style={{textAlign: 'center'}}>
      <div style={{fontSize: '32px', marginBottom: '10px'}}>📄</div>
   <p id="menu-file-name" style={{fontWeight: '500', margin: '5px 0'}}>
      {editingMenu && editingMenu.pdf_url ? 'Current PDF Menu' : newMenu.pdfFile ? newMenu.pdfFile.name : ''}
    </p>
      {editingMenu && editingMenu.pdf_url && (
        <a 
          href={editingMenu.pdf_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="sm-link"
          onClick={(e) => e.stopPropagation()}
        >
          View Current PDF
        </a>
      )}
      <p className="sm-help-text">Click to change file</p>
    </div>
  ) : (
    <div className="sm-upload-placeholder">
      <div className="sm-upload-icon">📄</div>
      <span>Click to upload a PDF</span>
      <span className="sm-upload-hint">PDF files up to 10MB</span>
    </div>
  )}
  <input
    type="file"
    id="menu-file"
    name="pdfFile"
    onChange={handleMenuChange}
    className="sm-file-input-hidden"
    accept="application/pdf"
    ref={menuFileInputRef}
  />
</div>
                {editingMenu && editingMenu.pdf_url && (
                  <div className="sm-current-file">
                    <p>Current PDF menu: 
                      <a 
                        href={editingMenu.pdf_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="sm-link"
                      >
                        View PDF
                      </a>
                    </p>
                    {editingMenu.thumbnail_url && (
                      <img 
                        src={editingMenu.thumbnail_url} 
                        alt="Menu thumbnail" 
                        className="sm-thumbnail" 
                      />
                    )}
                    <p className="sm-help-text">Upload a new PDF to replace the current one</p>
                  </div>
                )}
              </div>

                  <div className="sm-separator">
                    <span>OR</span>
                  </div>

                  <div className={`sm-form-group ${formErrors.weblink ? 'error' : ''}`}>
                    <label className="sm-label" htmlFor="menu-weblink">
                      Menu Web Link
                      <InfoTooltip 
                        text="URL to your online menu" 
                        variant="vendor"
                      />
                    </label>
                    <input
                      type="url"
                      id="menu-weblink"
                      name="weblink"
                      value={editingMenu ? editingMenu.weblink || '' : newMenu.weblink || ''}
                      onChange={handleMenuChange}
                      className="sm-input"
                      placeholder="https://example.com/menu"
                    />
                    {formErrors.weblink && (
                      <span className="sm-error-message">{formErrors.weblink}</span>
                    )}
                  </div>

                  <div className="sm-form-actions">
                    <button 
                      type="button" 
                      onClick={cancelEditing} 
                      className="sm-button secondary"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="sm-button primary"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <span className="sm-spinner"></span> 
                          {editingMenu ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingMenu ? 'Update Menu' : 'Create Menu'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Menus List */}
          {!isCreatingMenu && (
            <>
            <div className="sm-actions sm-actions-left">
              <button 
                type="button" 
                onClick={() => {
                  setIsCreatingMenu(true);
                  setIsCreatingSpecial(false);
                  setEditingMenu(null);
                  setEditingSpecial(null);
                }} 
                className="sm-button primary"
              >
                Create New Menu
              </button>
            </div>

              {menus.length === 0 ? (
                <div className="sm-empty-state">
                  <div className="sm-empty-icon">📋</div>
                  <h3>No menus yet</h3>
                  <p>Add your first menu to display to your customers</p>
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsCreatingMenu(true);
                      setIsCreatingSpecial(false);
                    }} 
                    className="sm-button primary"
                  >
                    Create First Menu
                  </button>
                </div>
              ) : (
                <div className="sm-grid">
                  {menus.map(menu => (
                    <div key={menu.id} className="sm-card menu-card">
                      <div className="sm-card-header">
                        <h3>Menu</h3>
                        <div className="sm-card-actions">
                          <button 
                            className="sm-button secondary small"
                            onClick={() => startEditingMenu(menu)}
                          >
                            Edit
                          </button>
                          <div className={`sm-menu-dropdown ${openMenuId === `menu-${menu.id}` ? 'open' : ''}`}>
                            <button 
                              className="sm-menu-button" 
                              onClick={(e) => handleMenuToggle(e, `menu-${menu.id}`)} 
                              aria-label="Menu options"
                            >
                              •••
                            </button>
                            <div className="sm-menu-content">
                              <button 
                                type="button" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMenu(menu.id);
                                }}
                                className="sm-menu-item danger"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="sm-card-body">
                        <div className="sm-menu-details">
                          {menu.thumbnail_url && (
                            <div className="sm-thumbnail-container">
                              <img src={menu.thumbnail_url} alt="Menu preview" className="sm-menu-thumbnail" />
                            </div>
                          )}
                          <div className="sm-menu-links">
                            {menu.pdf_url && (
                              <a 
                                href={menu.pdf_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="sm-button secondary small"
                              >
                                View PDF Menu
                              </a>
                            )}
                            {menu.weblink && (
                              <a 
                                href={menu.weblink} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="sm-button secondary small"
                              >
                                View Online Menu
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default SpecialsAndMenus;